import { App, Spin } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import FileDetail from '../../components/Files/FileDetail';
import { FileLimitationEnum } from '../../enums/FileLimitationEnum';
import type File from '../../interfaces/File';
import { useFileDownload } from '../../hooks/useFileDownload';
import { useFilesActions } from '../../hooks/useFilesActions';
import { useRightPanel } from '../../hooks/useRightPanel';
import { buildFilegrantViewerOpenUrl } from '../../lib/filegrantViewerChoice';
import {
  announceHostPostmessageReady,
  applyFilemanagerHostPreferences,
  extractHostPreferencesFromMessage,
  OfficePostMessageId,
  parseOfficePostMessageData,
  parseRequestFileActionValues,
  readFilemanagerHostPreferences,
  sendHostFileMetaToEditor,
  sendHostPreferencesToEditor,
} from '../../lib/officeGrantPostMessage/officePreferencesProtocol';
import { FileService } from '../../services/fileApiService';
import { useAppStore } from '../../store/store';
import { hasFileLimitation } from '../../utils/utils';
import { isViewerEmbedSession } from '../../utils/viewerWebviewAuth';

/** Contract sandbox (VIEWER_CONTRACT.md) — no top-navigation / popups-to-escape. */
const VIEWER_IFRAME_SANDBOX =
  'allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-downloads';

const VIEWER_IFRAME_ALLOW = 'clipboard-read; clipboard-write; fullscreen';

const fileService = new FileService();

export interface FilegrantViewerHostProps {
  viewerBaseUrl: string;
  /** Override route param — used by bottom drawer preview. */
  fileId?: string | null;
  version?: string | null;
  publicFileToken?: string | null;
  acToken?: string | null;
  isReadOnly?: boolean;
  /** Fill parent (drawer body) instead of route viewport minus header. */
  fillParent?: boolean;
  /** Prefer over `navigate(-1)` when guest sends `UI_Close` (drawer). */
  onRequestClose?: () => void;
}

/**
 * Thin host shell: iframes ViewerGrant `/open` and bridges postMessage file actions
 * back into FM (share/move/rename/… via useFilesActions).
 */
export default function FilegrantViewerHost({
  viewerBaseUrl,
  fileId: fileIdProp,
  version: versionProp,
  publicFileToken: publicFileTokenProp,
  acToken: acTokenProp,
  isReadOnly: isReadOnlyProp,
  fillParent = false,
  onRequestClose,
}: FilegrantViewerHostProps) {
  const { t } = useTranslation();
  const { modal, notification } = App.useApp();
  const navigate = useNavigate();
  const { fileId: fileIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isGuestLoading, setIsGuestLoading] = useState(true);
  const [hostFile, setHostFile] = useState<File | null>(null);

  const { handleDownloadFiles } = useFileDownload();
  const {
    handleShareFiles,
    handleMoveFiles,
    handleRenameFile,
    handleDeleteFiles,
    handleDownloadEncryptedFile,
  } = useFilesActions({});
  const { openRightPanel, setRightPanelContent, setRightPanelKind } = useRightPanel();
  const setViewerDrawerFile = useAppStore((s) => s.setViewerDrawerFile);
  const isViewerDrawerOpen = useAppStore((s) => s.isViewerDrawerOpen);

  const locale = useAppStore((s) => s.locale);
  const setLocale = useAppStore((s) => s.setLocale);
  const colorSchemePreference = useAppStore((s) => s.colorSchemePreference);
  const setColorSchemePreference = useAppStore((s) => s.setColorSchemePreference);
  const highContrastEnabled = useAppStore((s) => s.highContrastEnabled);
  const setHighContrastEnabled = useAppStore((s) => s.setHighContrastEnabled);
  const reduceMotionEnabled = useAppStore((s) => s.reduceMotionEnabled);
  const setReduceMotionEnabled = useAppStore((s) => s.setReduceMotionEnabled);

  const fileId = fileIdProp ?? fileIdParam;
  const publicFileToken =
    publicFileTokenProp !== undefined ? publicFileTokenProp : searchParams.get('publicFileToken');
  const hasPublicFileToken = publicFileToken != null && publicFileToken !== '';
  const acToken = acTokenProp !== undefined ? acTokenProp : searchParams.get('acToken');
  const version = versionProp !== undefined ? versionProp : searchParams.get('version');
  const isReadOnly =
    isReadOnlyProp !== undefined
      ? isReadOnlyProp
      : searchParams.get('r') === '1' || isViewerEmbedSession();

  const guestOrigin = useMemo(() => {
    try {
      return new URL(viewerBaseUrl).origin;
    } catch {
      return '';
    }
  }, [viewerBaseUrl]);

  const openUrl = useMemo(() => {
    if (!fileId) return '';
    const prefs = useAppStore.getState();
    return buildFilegrantViewerOpenUrl({
      viewerBaseUrl,
      fileId,
      refreshToken: prefs.customTokens?.rToken,
      isReadOnly,
      version,
      publicFileToken,
      acToken,
      preferences: {
        locale: prefs.locale,
        colorSchemePreference: prefs.colorSchemePreference,
        highContrastEnabled: prefs.highContrastEnabled,
        reduceMotionEnabled: prefs.reduceMotionEnabled,
      },
    });
  }, [viewerBaseUrl, fileId, isReadOnly, version, publicFileToken, acToken]);

  const canShareFile = useMemo(() => {
    if (!hostFile) return false;
    return !hasFileLimitation(hostFile.fileLimitations, FileLimitationEnum.DenyFileShare);
  }, [hostFile]);

  const canMoveFile = useMemo(() => {
    if (!hostFile) return false;
    return !hasFileLimitation(hostFile.fileLimitations, FileLimitationEnum.DenyFileMove);
  }, [hostFile]);

  const canRenameFile = useMemo(() => {
    if (!hostFile) return false;
    return !hasFileLimitation(hostFile.fileLimitations, FileLimitationEnum.DenyFileRename);
  }, [hostFile]);

  const canDeleteFile = useMemo(() => {
    if (!hostFile) return false;
    return !hasFileLimitation(hostFile.fileLimitations, FileLimitationEnum.DenyFileDelete);
  }, [hostFile]);

  const canDownloadFile = useMemo(() => {
    if (!hostFile) return false;
    return !hasFileLimitation(hostFile.fileLimitations, FileLimitationEnum.DenyFileDownload);
  }, [hostFile]);

  // Host metadata for postMessage actions (guest loads bytes itself).
  useEffect(() => {
    if (!fileId || hasPublicFileToken) return;
    let cancelled = false;
    void (async () => {
      try {
        const fileRes = await fileService.getSpecificFiles([Number(fileId)]);
        const located = fileRes.myFiles?.[0];
        if (cancelled || !located) return;
        setHostFile(located);
      } catch {
        // Guest can still open; host actions stay gated until meta arrives.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fileId, hasPublicFileToken]);

  // Keep drawer snapshot fresh so FilesHeader Details/Chat sync has a file.
  useEffect(() => {
    if (!hostFile || !isViewerDrawerOpen) return;
    setViewerDrawerFile(hostFile);
  }, [hostFile, isViewerDrawerOpen, setViewerDrawerFile]);

  const handleOpenMoveModal = useCallback(async () => {
    if (!hostFile || hasPublicFileToken || !canMoveFile) return;
    try {
      const detail = await fileService.getFile(hostFile.id);
      const rawOwnerId = detail.ownerId;
      const ownerId =
        typeof rawOwnerId === 'string' ? parseInt(rawOwnerId, 10) : Number(rawOwnerId);
      if (!Number.isFinite(ownerId)) {
        notification.error({
          title: t('errors.genericFetchError', { resource: t('common.file') }),
        });
        return;
      }
      handleMoveFiles([{ ...hostFile, ownerId }], undefined, undefined);
    } catch {
      notification.error({
        title: t('errors.genericFetchError', { resource: t('common.file') }),
      });
    }
  }, [canMoveFile, handleMoveFiles, hasPublicFileToken, hostFile, notification, t]);

  const handleHostRename = useCallback(
    (source: MessageEventSource | null, origin: string) => {
      if (!hostFile || !canRenameFile || hasPublicFileToken) {
        notification.error({ title: t('errors.forbidden') });
        return;
      }
      handleRenameFile(hostFile, {
        onFilesUpdated: (updated) => {
          const renamed = updated[0];
          if (!renamed) return;
          setHostFile((prev) => (prev ? { ...prev, fileName: renamed.fileName } : prev));
          sendHostFileMetaToEditor(source, origin, {
            fileId: renamed.id,
            fileName: renamed.fileName,
          });
        },
      });
    },
    [canRenameFile, handleRenameFile, hasPublicFileToken, hostFile, notification, t],
  );

  const handleHostDelete = useCallback(() => {
    if (!hostFile || !canDeleteFile || hasPublicFileToken) {
      notification.error({ title: t('errors.forbidden') });
      return;
    }
    modal.confirm({
      title: t('modal.officeEditorDelete.title'),
      content: t('modal.officeEditorDelete.description'),
      okText: t('common.delete'),
      cancelText: t('common.cancel'),
      centered: true,
      onOk: () => {
        handleDeleteFiles([hostFile], {
          onFilesDeleted: () => {
            if (onRequestClose) {
              onRequestClose();
              return;
            }
            navigate('/files/personal');
          },
        });
      },
    });
  }, [
    canDeleteFile,
    handleDeleteFiles,
    hasPublicFileToken,
    hostFile,
    modal,
    navigate,
    notification,
    onRequestClose,
    t,
  ]);

  const handleHostDetails = useCallback(() => {
    if (!hostFile) return;
    setRightPanelKind('detail');
    setRightPanelContent(<FileDetail file={hostFile} />);
    openRightPanel();
  }, [hostFile, openRightPanel, setRightPanelContent, setRightPanelKind]);

  const announceReady = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    if (!win || !guestOrigin) return;
    const hostPreferences = readFilemanagerHostPreferences({
      locale,
      colorSchemePreference,
      highContrastEnabled,
      reduceMotionEnabled,
    });
    announceHostPostmessageReady(win, guestOrigin, hostPreferences);
    if (hostFile) {
      sendHostFileMetaToEditor(win, guestOrigin, {
        fileId: hostFile.id,
        fileName: hostFile.fileName,
      });
    }
  }, [
    colorSchemePreference,
    guestOrigin,
    highContrastEnabled,
    hostFile,
    locale,
    reduceMotionEnabled,
  ]);

  useEffect(() => {
    if (!guestOrigin) return;

    const preferenceActions = {
      setLocale,
      setColorSchemePreference,
      setHighContrastEnabled,
      setReduceMotionEnabled,
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== guestOrigin) return;

      let payload = event.data;
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch {
          return;
        }
      }

      const data = parseOfficePostMessageData(payload);
      if (!data) return;

      const hostPreferences = readFilemanagerHostPreferences({
        locale,
        colorSchemePreference,
        highContrastEnabled,
        reduceMotionEnabled,
      });

      switch (data.MessageId) {
        case 'App_LoadingStatus': {
          const status = data.Values?.Status;
          if (status === 'ready' || status === 'Document_Loaded') {
            setIsGuestLoading(false);
          } else if (status === 'error') {
            setIsGuestLoading(false);
          } else if (status === 'loading') {
            setIsGuestLoading(true);
          }
          break;
        }
        case 'UI_Close':
          if (onRequestClose) {
            onRequestClose();
          } else {
            navigate(-1);
          }
          break;
        case OfficePostMessageId.AppPreferencesRequest:
          sendHostPreferencesToEditor(event.source, event.origin, hostPreferences);
          break;
        case OfficePostMessageId.AppPreferences: {
          const childPreferences = extractHostPreferencesFromMessage(data);
          if (childPreferences) {
            applyFilemanagerHostPreferences(childPreferences, preferenceActions);
          }
          break;
        }
        case OfficePostMessageId.AppRequestFileAction: {
          const request = parseRequestFileActionValues(data.Values);
          if (!request || !hostFile || request.fileId !== hostFile.id) {
            break;
          }
          switch (request.action) {
            case 'share':
              if (!canShareFile) {
                notification.error({ title: t('errors.forbidden') });
                break;
              }
              handleShareFiles([hostFile]);
              break;
            case 'move':
              if (!canMoveFile || hasPublicFileToken) {
                notification.error({ title: t('errors.forbidden') });
                break;
              }
              void handleOpenMoveModal();
              break;
            case 'rename':
              handleHostRename(event.source, event.origin);
              break;
            case 'delete':
              handleHostDelete();
              break;
            case 'details':
              handleHostDetails();
              break;
            case 'download':
              if (!canDownloadFile) {
                notification.error({ title: t('errors.forbidden') });
                break;
              }
              handleDownloadFiles([hostFile]);
              break;
            case 'downloadEncrypted':
              if (!canDownloadFile) {
                notification.error({ title: t('errors.forbidden') });
                break;
              }
              void handleDownloadEncryptedFile(hostFile);
              break;
            default:
              break;
          }
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [
    canDownloadFile,
    canMoveFile,
    canShareFile,
    colorSchemePreference,
    guestOrigin,
    handleDownloadEncryptedFile,
    handleDownloadFiles,
    handleHostDelete,
    handleHostDetails,
    handleHostRename,
    handleOpenMoveModal,
    handleShareFiles,
    hasPublicFileToken,
    highContrastEnabled,
    hostFile,
    locale,
    navigate,
    notification,
    onRequestClose,
    reduceMotionEnabled,
    setColorSchemePreference,
    setHighContrastEnabled,
    setLocale,
    setReduceMotionEnabled,
    t,
  ]);

  // Re-announce prefs / meta when host file arrives after iframe already loaded.
  useEffect(() => {
    if (!hostFile || isGuestLoading) return;
    announceReady();
  }, [announceReady, hostFile, isGuestLoading]);

  const openingLabel = t('viewer.openingFile', { defaultValue: 'Opening file…' });

  const loadingOverlay = (
    <div
      className="absolute inset-0 z-10 flex h-full min-h-full w-full flex-col items-center justify-center gap-3 bg-white/90"
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-testid="filegrant-viewer-loading"
    >
      <Spin size="large" spinning />
      <p className="text-gray-dark m-0 text-sm font-medium">{openingLabel}</p>
    </div>
  );

  if (!openUrl || !guestOrigin) {
    return (
      <div
        className={
          // fillParent: h-full (parent is not a flex column — flex-1 alone collapses to 0).
          fillParent ? 'relative h-full min-h-0 w-full' : 'relative h-full min-h-[12rem] w-full'
        }
      >
        {loadingOverlay}
      </div>
    );
  }

  // fillParent: h-full in drawer row stretch; flex-1 alone was height 0 (no flex-col parent).
  const shellClassName = fillParent
    ? 'relative h-full min-h-0 w-full overflow-hidden'
    : 'relative h-[calc(100dvh-var(--app-header-stack-height,3.5rem))] w-full overflow-hidden';

  return (
    <main className={shellClassName}>
      {isGuestLoading ? loadingOverlay : null}
      <iframe
        ref={iframeRef}
        data-testid="filegrant-viewer-iframe"
        title={hostFile?.fileName || 'Filegrant Viewer'}
        src={openUrl}
        className="absolute inset-0 block h-full w-full border-none"
        allowFullScreen
        allow={VIEWER_IFRAME_ALLOW}
        sandbox={VIEWER_IFRAME_SANDBOX}
        onLoad={announceReady}
      />
    </main>
  );
}
